import axios, { AxiosResponse } from 'axios';
import { find, flow, get, getOr, isArray, map, omit, size, some, startsWith, toInteger } from 'lodash/fp';
import { AppConfig, defaultToastOptions, OdooUserType, proxyUsers, User } from './Global';
import { ToastOptions } from '@ionic/react';
import { Buffer } from 'buffer';
import {
    OdooPointing,
    OdooLeave,
    OdooProvisionalCalendar,
    Pointing,
    Leave,
    PointingToSaved,
    ProvisionalWithRecurrence,
    ProvisionalCalendarToSaved,
    LeaveToSaved,
    MetaDataItem,
} from '../types/models';
import { dateFromOdoo, dateTimeToOdooDatetime } from './date';
import { DateTime } from 'luxon';
import { Preferences } from '@capacitor/preferences';

export interface SearchReadParams<T> {
    fields: Readonly<Array<keyof T>>;
    domain?: any[];
    context?: any;
    offset?: number;
    limit?: number;
    sort?: string;
}

interface ReadGroupParams<T> {
    fields: Readonly<Array<keyof T>>;
    domain?: any[];
    context?: any;
    offset?: number;
    limit?: number;
    sort?: string;
    groupBy: string[];
}

export type WithAccesRight<T> = T & {
    _access_right: {
        read: boolean;
        write: boolean;
        create: boolean;
        unlink: boolean;
    };
};

export type BasicOdooObject = { id: number; name: string };
export type RelationalBasicObject = BasicOdooObject | false;

interface CallKwParams {
    method: string;
    args: any[];
    kwargs?: any;
}

const preventEmpty = (_target: Object, _propertyKey: string, descriptor: PropertyDescriptor) => {
    const originalMethod = descriptor.value;

    descriptor.value = function (...args: any) {
        if (!(this as Odoo).activeUser) {
            throw new Error('No activeUser currently !');
        }
        return originalMethod.apply(this, args);
    };
};

// TODO: Gérer la langue et la timezone

export class Odoo {
    activeUser: User | null = null;

    async init() {
        let pUsers = await Preferences.get({ key: 'users' });

        if (pUsers.value) {
            try {
                this.activeUser = JSON.parse(pUsers.value)?.mainUser || null;

                this.checkSession()
                    .then(() => {
                        console.debug('Odoo session restored properly');
                        this.refreshToken();
                    })
                    .catch(async (err) => {
                        await Preferences.remove({ key: 'users' });
                        console.log('Error restoring session', err);
                        window.location.reload();
                    });
            } catch {}
        }
    }

    async _rpc(baseUrl: string, url: string, params: any, jwt?: string): Promise<AxiosResponse<any>> {
        const _params = {
            ...params,
            context: { ...params?.context, lang: 'fr_FR', tz: 'Europe/Paris' },
            kwargs: {
                ...params?.kwargs,
                context: { ...params?.kwargs?.context, lang: 'fr_FR', tz: 'Europe/Paris', ...params.context },
            },
        };
        return axios.post(
            `${baseUrl}${url}`,
            {
                jsonrpc: '2.0',
                method: 'call',
                id: Math.floor(Math.random() * 1000 * 1000 * 1000),
                params: _params,
            },
            {
                timeout: 60000,
                headers: {
                    JwtToken: jwt,
                },
            }
        );
    }

    @preventEmpty
    async rpc(url: string, params: any): Promise<AxiosResponse<any>> {
        return this._rpc(this.activeUser!.baseUrl, url, params, this.activeUser!.jwt);
    }

    sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

    async getHostData(login: string): Promise<{ url: string; db: string }> {
        // For development purposes only
        if (import.meta.env.VITE_FORCE_ODOO_DB && import.meta.env.VITE_FORCE_ODOO_HOST) {
            return {
                url: import.meta.env.VITE_FORCE_ODOO_HOST,
                db: import.meta.env.VITE_FORCE_ODOO_DB,
            };
        }

        const { data } = await axios.get<{ url: string; db: string }>(
            `https://odoo-hub.tetra-info.com/get-host-data/${login}`,
            {
                headers: {
                    Authorization: 'UcOitCKTZ1A1pTHBUPjt2yENrIuJHjl4iEyZdeuq3Isxigf25sShNbPAxFC1hXK7',
                },
            }
        );

        return data;
    }

    _getParsedToken(token: string) {
        return JSON.parse(Buffer.from(token.split('.')[1], 'base64').toString());
    }

    _formatToken(token: string, login: string, url: string) {
        const payload = this._getParsedToken(token);

        return {
            uid: payload.uid as number,
            login: login,
            jwt: token,
            // We do that to handle proxy localhost for development purposes
            baseUrl: startsWith('http://localhost:', payload.base_url as string) ? url : (payload.base_url as string),
            db: payload.db as string,
            type: payload.type as OdooUserType,
            dbuuid: payload.dbuuid as string,
        };
    }

    async _login(login: string, password: string, url: string, db: string): Promise<User> {
        const res = await this._rpc(url, '/web/session/authenticate', {
            login: login,
            password: password,
            db: db,
        });

        this.handleError(res);

        return this._formatToken(res.data.result.jwt_token, login, url);
    }

    async login(login: string, password: string): Promise<User> {
        const hostData = await this.getHostData(login);

        return this._login(login, password, hostData.url, hostData.db);
    }

    logout = async () => {
        const response = await this.rpc('/web/session/destroy', {});
        this.handleError(response);
        return response.data;
    };

    getSession = async () => {
        const response = await this.rpc('/web/session/get_session_info', {});
        this.handleError(response);
        return response.data;
    };

    checkSession = async () => {
        const response = await this.rpc('/web/session/check', {});
        try {
            this.handleError(response, true);
            return true;
        } catch (e) {
            throw e;
        }
    };

    needRefreshToken = () => {
        if (proxyUsers.mainUser) {
            let payload = this._getParsedToken(proxyUsers.mainUser.jwt);

            if (payload?.valid_until) {
                console.log('Legacy token, force refresh');
                return true;
            }

            if (payload.exp < DateTime.now().plus({ days: 5 }).toSeconds()) {
                return true;
            }
        }

        return false;
    };

    refreshToken = async () => {
        try {
            if (this.needRefreshToken()) {
                console.log('Refreshing jwt ...');

                let session = await this.getSession();

                let user = this._formatToken(
                    session.result.jwt_token,
                    proxyUsers.mainUser?.login || '',
                    proxyUsers.mainUser?.baseUrl || ''
                );

                proxyUsers.mainUser = user;
            }
        } catch {
            console.error('Error refreshing jwt');
        }
    };

    callKw = async (model: string, { method, args = [], kwargs = {} }: CallKwParams) => {
        const response = await this.rpc(`/web/dataset/call_kw/${model}/${method}`, {
            model,
            method,
            args,
            kwargs,
        });

        this.handleError(response);

        return response;
    };

    readGroup = async <T>(
        model: string,
        { fields, domain = [], context, offset, limit, sort, groupBy }: ReadGroupParams<T>
    ) => {
        const response = await this.callKw(model, {
            method: 'read_group',
            args: [],
            kwargs: {
                fields,
                domain,
                context,
                offset,
                limit,
                sort,
                groupby: groupBy,
            },
        });

        return response.data.result;
    };
    private _searchRead = async <T>(
        model: string,
        { fields, domain = [], context, offset, limit, sort }: SearchReadParams<T>
    ) => {
        const response = await this.rpc('/web/dataset/search_read', {
            model,
            fields,
            domain,
            context,
            offset,
            limit,
            sort,
        });

        this.handleError(response);

        return response;
    };

    async searchRead<T>(model: string, params: SearchReadParams<T>): Promise<Array<{ [key in keyof T]: any }>>;
    async searchRead<T>(
        model: string,
        params: SearchReadParams<T>,
        add_access_right: boolean
    ): Promise<Array<WithAccesRight<{ [key in keyof T]: any }>>>;
    async searchRead<T>(
        model: string,
        params: SearchReadParams<T>,
        add_access_right: boolean = false
    ): Promise<Array<{ [key in keyof T]: any } | WithAccesRight<{ [key in keyof T]: any }>>> {
        const response = await this._searchRead(model, {
            ...params,
            context: { ...params.context, add_access_right },
        });
        return response.data.result.records;
    }

    async searchReadOne<T>(model: string, params: SearchReadParams<T>): Promise<{ [key in keyof T]: any } | null>;
    async searchReadOne<T>(
        model: string,
        params: SearchReadParams<T>,
        add_access_right: boolean
    ): Promise<WithAccesRight<{ [key in keyof T]: any }> | null>;
    async searchReadOne<T>(
        model: string,
        params: SearchReadParams<T>,
        add_access_right: boolean = false
    ): Promise<{ [key in keyof T]: any } | WithAccesRight<{ [key in keyof T]: any }> | null> {
        const response = await this._searchRead(model, {
            ...params,
            limit: 1,
            context: { ...params.context, add_access_right },
        });

        if (size(response.data.result.records) === 0) {
            return null;
        }

        return response.data.result.records[0];
    }

    write = async (model: string, ids: number | number[], values: any, kwargs?: any) => {
        await this.callKw(model, {
            method: 'write',
            args: [isArray(ids) ? ids : [ids], values],
            kwargs: kwargs || {},
        });
    };

    read = async (model: string, ids: number | number[], fields: Readonly<string[]>) => {
        const response = await this.callKw(model, {
            method: 'read',
            args: [isArray(ids) ? ids : [ids], fields],
        });

        return response.data.result;
    };

    create = async (model: string, values: any, kwargs?: {}): Promise<number> => {
        const response = await this.callKw(model, {
            method: 'create',
            args: [values],
            kwargs: kwargs || {},
        });

        return response.data.result;
    };

    unlink = async (model: string, ids: number | number[], context?: {}) => {
        await this.callKw(model, {
            method: 'unlink',
            args: [isArray(ids) ? ids : [ids]],
            kwargs: { context },
        });
    };

    createRead = async (model: string, fields: string[], values: any, context?: {}) => {
        const ids = await this.create(model, values, context);
        return await this.read(model, ids, fields);
    };

    handleError = (response: AxiosResponse<any>, silent = false) => {
        let hasErrors1 = flow(getOr([], 'data.result.messages'), some({ type: 'error' }))(response as any);
        let hasErrors2 = get('data.error', response);
        if (hasErrors1 || hasErrors2) {
            var error: any;
            if (!silent) console.error(response.config.url ?? '');
            if (!silent) console.error(response.config.data);
        }

        if (get('data.error.message', response) === 'Odoo Session Expired') {
            proxyUsers.mainUser = null;
            console.error('Odoo Session Expired for mainUser');
            console.log('Resseting mainUser in proxyUsers');
        }

        if (hasErrors1) {
            console.log('handleError1');
            error = new Error('There is an error in the response from the service');
            error.errors = map((m) => m.message, response.data.result.messages);
            if (!silent) console.error(error.errors);
            error.messages = this.getErrorMessages(error.errors);
            console.log(error.messages);
            throw error;
        }

        if (hasErrors2) {
            console.log('handleError2');
            error = new Error('There is an error in the response from the service');
            error.errors = JSON.stringify(response.data.error);
            if (!silent) console.error(error.errors);
            const msg =
                response.data.error.data.exception_type == 'validation_error'
                    ? response.data.error.data.arguments[0]
                    : response.data.error.data.message;
            error.messages = this.getErrorMessages([msg]);
            throw error;
        }
    };

    getErrorMessages = (errors: string[]): ToastOptions[] => {
        const _default = { ...defaultToastOptions, color: 'danger', duration: 5000 };
        return errors.map((e) => {
            let regex = e.match(/Invalid field '(.*)' on model '(.*)'/);
            if (regex)
                return {
                    ..._default,
                    message: 'Champ ' + regex[1] + ' invalide sur le modèle ' + regex[2],
                };

            regex = e.match(
                /The requested operation \("(?<operation>.*)" on "(?<modelLabel>.*)" \((?<model>.*)\)\).*Records.*id=(?<id>.*)\),/s
            );
            if (regex) {
                const convertOperation = (operation: string | undefined) => {
                    switch (operation) {
                        case 'unlink':
                            return 'suppression';
                        case 'write':
                            return 'modification';
                        case 'create':
                            return 'création';
                        case 'read':
                            return 'lecture';
                    }
                    return operation;
                };
                return {
                    ..._default,
                    message: `Impossible d'effectuer l'opération "${convertOperation(
                        regex.groups?.operation
                    )}" sur le modèle "${regex.groups?.modelLabel}" (id=${regex.groups?.id})`,
                    duration: 3000,
                };
            }
            return { ..._default, message: e };
        });
    };

    getAppConfig: () => Promise<AppConfig> = async () => {
        const configParameters = await this.searchRead<{ id: number; key: string; value: string }>(
            'ir.config_parameter',
            {
                fields: ['key', 'value'],
                domain: [
                    [
                        'key',
                        'in',
                        [
                            'hr_madper_app_pointing_mode',
                            'hr_madper_app_pointing_rounding_hours',
                            'hr_madper_app_pointing_limit_lunch_to_one',
                            'hr_madper_app_pointing_limit_pointing_by_day',
                        ],
                    ],
                ],
            }
        );

        return {
            appMode: find({ key: 'hr_madper_app_pointing_mode' }, configParameters)?.value || 'live',
            limitLunchToOne:
                find({ key: 'hr_madper_app_pointing_limit_lunch_to_one' }, configParameters)?.value === 'True',
            roundingHours:
                toInteger(find({ key: 'hr_madper_app_pointing_rounding_hours' }, configParameters)?.value) || 0,
            limitPointingByDay:
                find({ key: 'hr_madper_app_pointing_limit_pointing_by_day' }, configParameters)?.value || 0,
        };
    };
}

const odoo = new Odoo();

export const fetcherSearchRead = <T>([model, params]: [string, SearchReadParams<T>]) => odoo.searchRead(model, params);
export const fetcherSearchReadOne = <T>([model, params]: [string, SearchReadParams<T>]) =>
    odoo.searchReadOne(model, params);

export const fetcherSearchReadWithAccess = <T>([model, params]: [string, SearchReadParams<T>]) =>
    odoo.searchRead(model, params, true);

export const fetcherReadGroup = <T>([model, params]: [string, ReadGroupParams<T>]) => odoo.readGroup(model, params);
export const fetcherController = ([url]: [string]): any => odoo.rpc(url, {});

export const saveOrCreate = async <T extends { [key: string]: any }, R = any>(
    model: string,
    { id, ...value }: T,
    fields: Readonly<string[]>,
    context?: {}
): Promise<R[]> => {
    id = await saveOrCreateWithoutRead(model, { id, ...value }, context);
    return odoo.read(model, id, fields);
};

export const unlink = async (model: string, ids: number | number[], context?: {}): Promise<void> => {
    return await odoo.unlink(model, ids, context);
};

export const saveOrCreateWithoutRead = async <T extends { [key: string]: any } = any>(
    model: string,
    { id, ...value }: T,
    context?: {}
) => {
    if (!id || id === -1) {
        id = await odoo.create(model, value, { context: context });
    } else {
        await odoo.write(model, id, value, { context: context });
    }
    return id;
};

export const saveMulti = async <T extends { [key: string]: any } = any>(
    model: string,
    ids: number[],
    { ...value }: T,
    context?: {}
) => {
    await odoo.write(model, ids, omit(['id'], value), { context: context });
    return ids;
};

export const standardizedOdooPointing = (p: OdooPointing): Pointing => ({
    ...p,
    real_time_start: p.real_time_start ? dateFromOdoo(p.real_time_start) : null,
    real_time_end: p.real_time_end ? dateFromOdoo(p.real_time_end) : null,
    input_time_start: p.input_time_start ? dateFromOdoo(p.input_time_start) : null,
    input_time_end: p.input_time_end ? dateFromOdoo(p.input_time_end) : null,
    employee_id: p.employee_id ? p.employee_id[0] : 0,
    mad_id: p.mad_id ? p.mad_id[0] : 0,
    workplace_id: p.workplace_id ? p.workplace_id[0] : 0,
    metadata: p.metadata ? JSON.parse(p.metadata) : { start: null, end: null, confirm: null },
    user_employee_validation_date: p.user_employee_validation_date
        ? dateFromOdoo(p.user_employee_validation_date)
        : null,
    user_adherent_validation_date: p.user_adherent_validation_date
        ? dateFromOdoo(p.user_adherent_validation_date)
        : null,
    user_geiq_validation_date: p.user_geiq_validation_date ? dateFromOdoo(p.user_geiq_validation_date) : null,
    origin_id: p.origin_id ? p.origin_id[0] : 0,
});

export const standardizedOdooPointings = (pointings: WithAccesRight<OdooPointing>[]): Pointing[] =>
    map(standardizedOdooPointing, pointings);

export const standardizedOdooLeaves = (leaves: WithAccesRight<OdooLeave>[]): Leave[] =>
    map(standardizedOdooLeave, leaves);

export const unstandardizedOdooPointing = (p: Pointing): PointingToSaved => ({
    ...omit(['_access_right'], p),
    real_time_start: dateTimeToOdooDatetime(p.real_time_start),
    real_time_end: dateTimeToOdooDatetime(p.real_time_end),
    input_time_start: dateTimeToOdooDatetime(p.input_time_start),
    input_time_end: dateTimeToOdooDatetime(p.input_time_end),
    employee_id: p.employee_id > 0 ? p.employee_id : false,
    mad_id: p.mad_id > 0 ? p.mad_id : false,
    workplace_id: p.workplace_id > 0 ? p.workplace_id : false,
    metadata: JSON.stringify(p.metadata),
    user_employee_validation_date: dateTimeToOdooDatetime(p.user_employee_validation_date),
    user_adherent_validation_date: dateTimeToOdooDatetime(p.user_adherent_validation_date),
    user_adherent_id: p.user_adherent_id && p.user_adherent_id[0] > 0 ? p.user_adherent_id[0] : false,
    user_geiq_validation_date: dateTimeToOdooDatetime(p.user_geiq_validation_date),
    user_geiq_id: p.user_geiq_id && p.user_geiq_id[0] > 0 ? p.user_geiq_id[0] : false,
    origin_id: p.origin_id > 0 ? p.origin_id : false,
});

export const standardizedOdooProvisionalWithRecurrence = (p: OdooProvisionalCalendar): ProvisionalWithRecurrence => ({
    ...p,
    employee_id: p.employee_id[0],
    workplace_id: p.workplace_id[0],
    mad_id: p.mad_id[0],
    date_start: dateFromOdoo(p.date_start),
    date_end: dateFromOdoo(p.date_end),
    date_end_recurrent: dateFromOdoo(p.date_end),
    recurrence_type: 'day',
    is_recurrent: false,
});

export const unstandardizedOdooProvisionalWithRecurrence = (
    p: ProvisionalWithRecurrence
): ProvisionalCalendarToSaved => ({
    ...p,
    date_start: p.date_start.toUTC().toFormat('yyyy-MM-dd HH:mm:ss'),
    date_end: p.date_end.toUTC().toFormat('yyyy-MM-dd HH:mm:ss'),
    employee_id: p.employee_id > 0 ? p.employee_id : false,
    mad_id: p.mad_id > 0 ? p.mad_id : false,
    workplace_id: p.workplace_id > 0 ? p.workplace_id : false,
});

export default odoo;

export const standardizedOdooLeave = (l: OdooLeave): Leave => ({
    ...l,
    name: '',
    date_start: l.date_start ? DateTime.fromSQL(l.date_start, { zone: 'local' }) : null,
    date_end: l.date_end ? DateTime.fromSQL(l.date_end, { zone: 'local' }).endOf('day') : null,
    employee_id: l.employee_id ? l.employee_id[0] : 0,
    user_employee_validation_date: l.user_employee_validation_date
        ? dateFromOdoo(l.user_employee_validation_date)
        : null,
    user_adherent_validation_date: l.user_adherent_validation_date
        ? dateFromOdoo(l.user_adherent_validation_date)
        : null,
    user_geiq_validation_date: l.user_geiq_validation_date ? dateFromOdoo(l.user_geiq_validation_date) : null,
    employee_note: l.employee_note || '',
    adherent_note: l.adherent_note || '',
    geiq_note: l.geiq_note || '',
});

export const unstandardizedOdooLeave = (l: Leave, metadata: MetaDataItem): LeaveToSaved => ({
    ...omit(['_access_right'], l),
    date_start: l.date_start?.toISODate() || null,
    date_end: l.date_end?.toISODate() || null,
    employee_id: l.employee_id > 0 ? l.employee_id : false,
    user_employee_validation_date: dateTimeToOdooDatetime(l.user_employee_validation_date),
    user_adherent_validation_date: dateTimeToOdooDatetime(l.user_adherent_validation_date),
    user_geiq_validation_date: dateTimeToOdooDatetime(l.user_geiq_validation_date),
    user_adherent_id: l.user_adherent_id && l.user_adherent_id[0] > 0 ? l.user_adherent_id[0] : false,
    user_geiq_id: l.user_geiq_id && l.user_geiq_id[0] > 0 ? l.user_geiq_id[0] : false,
    metadata: JSON.stringify(metadata),
});
